import { MapPin, Drop, QrCode, MapTrifold } from '@phosphor-icons/react';
import type { PiezaFormaleta } from '@/api/formaleta';
import { Fecha, Foto, Pastilla, SinRegistrar } from './comunes';

// La tarjeta de UNA pieza física. Es el modo que Daniel pidió al lado del
// agrupado: «al fin y al cabo, las personas van a buscar las piezas
// puntualmente, no las agrupaciones […] buscar una pieza que necesite y ver si
// hay alguna disponible o, si no hay disponible, cuál es la más cercana o dónde
// están».
//
// Lo primero que se lee es el CONSECUTIVO, en mono y grande: es lo que está
// marcado en el aluminio y lo único que une la pieza en la mano con la fila de
// la base.

export function TarjetaPieza({ pieza, compacta }: {
  pieza: PiezaFormaleta;
  /** Dentro del despliegue de un modelo no se repite el nombre del modelo. */
  compacta?: boolean;
}) {
  return (
    <article className="flex items-stretch gap-0 overflow-hidden rounded-lg border border-brand-gray-200 bg-white">
      {/* La barra de color de la categoría: identidad visual a distancia, antes
          de leer una sola letra. */}
      <span
        style={{ backgroundColor: pieza.colorHex || '#D9D9D9' }}
        className="w-1.5 shrink-0"
      />

      <div className="flex min-w-0 flex-1 items-center gap-3 p-2.5">
        {!compacta && <Foto url={pieza.urlFoto} alt={pieza.modelo || 'pieza'} lado={52} />}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-mono text-base font-bold leading-none text-brand-gray-500">
              {pieza.consecutivo || <SinRegistrar que="sin consecutivo" />}
            </p>
            {pieza.abreviatura
              ? <Pastilla texto={pieza.abreviatura} colorHex={pieza.colorHex} titulo={pieza.categoria || undefined} />
              : <SinRegistrar que="sin categoría" detalle="El renglón no tiene IDFormCategoria: son los accesorios, el 93% de las piezas." />}
            {pieza.estado
              ? <Pastilla texto={pieza.estado} colorHex={pieza.colorEstado} />
              : <SinRegistrar que="sin estado" detalle="IDFormEstadoPieza en NULL." />}
          </div>

          {!compacta && (
            // El nombre crudo del fabricante va SECUNDARIO y en mono, nunca
            // como identificador principal: es jerga técnica en inglés que la
            // cuadrilla no usa.
            <p className="mt-1 truncate font-mono text-[11px] text-brand-gray-400"
              title={pieza.modelo || undefined}>
              {pieza.modelo || '—'}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <MapPin size={12} weight="bold" />
              {pieza.idObra || pieza.ubicacion
                ? <span className="font-medium text-brand-gray-500">
                    {[pieza.idObra, pieza.ubicacion].filter(Boolean).join(' · ')}
                  </span>
                // «¿dónde está?» es una de las dos preguntas de campo, y hoy
                // FormMovimiento tiene 0 filas: el paradero empieza a existir
                // cuando se registre el primer movimiento.
                : <SinRegistrar que="sin paradero" detalle="No se ha registrado ningún movimiento de esta pieza." />}
            </span>

            <LotePieza pieza={pieza} />

            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <Drop size={12} weight="bold" />
              <Fecha valor={pieza.fechaUltLavado} que="sin lavado" />
            </span>

            <span className="flex items-center gap-1 text-[11px] text-brand-gray-400">
              <QrCode size={12} weight="bold" />
              {pieza.fechaMarcado
                ? <Fecha valor={pieza.fechaMarcado} que="sin marcar" />
                // NULL = está en la base pero la pieza en la mano sigue siendo
                // anónima. Es la diferencia entre inventariar y marcar.
                : <SinRegistrar que="sin marcar" detalle="La pieza existe en la base pero no tiene marca física." />}
            </span>
          </div>
        </div>

        {pieza.latitud !== null && pieza.longitud !== null && (
          <a
            href={`https://www.google.com/maps?q=${pieza.latitud},${pieza.longitud}`}
            target="_blank" rel="noreferrer"
            title="Abrir la coordenada del último movimiento"
            className="shrink-0 rounded border border-brand-gray-200 p-2 text-brand-gray-400 hover:border-brand-gray-300 hover:text-brand-gray-500"
          >
            <MapPin size={16} weight="bold" />
          </a>
        )}
      </div>
    </article>
  );
}

/**
 * El lote resuelto desde el GPS (F-4). Son TRES estados visibles distintos y
 * confundirlos es justo el error que F-4 evita:
 *
 *   · lote CONFIABLE → el código, firme.
 *   · lote DUDOSO    → el mismo código con `≈`. El barrido lo resolvió, pero el
 *     margen contra el lote vecino no le gana al error del GPS: el lote de al
 *     lado es indistinguible. Mostrarlo como certero seria mentir con cara de dato.
 *   · barrido SIN lote → «fuera de lote», que es información real.
 *
 * Y cuando `fechaLote` es null no se pinta NADA: el barrido todavía no pasó por
 * esta pieza, que es distinto de haber pasado y no encontrar nada.
 */
function LotePieza({ pieza }: { pieza: PiezaFormaleta }) {
  if (!pieza.fechaLote) return null;

  if (!pieza.lote) {
    return (
      <span className="flex items-center gap-1 text-[11px] text-brand-gray-400"
        title="El barrido corrió y la coordenada no cayó dentro de ningún lote ni cerca de uno.">
        <MapTrifold size={12} weight="bold" />
        fuera de lote
      </span>
    );
  }

  const dudoso = !pieza.loteConfiable;
  const detalle = dudoso
    ? `Lote DUDOSO: ${pieza.lote}. El siguiente lote está a solo ${pieza.loteMargenM ?? '?'} m `
      + `más lejos y el GPS traía un error mayor, así que el lote vecino es indistinguible. `
      + `Verificar en campo antes de usarlo para una decisión.`
    : `Lote ${pieza.lote}`
      + (pieza.loteDistanciaM === 0 ? ' · la coordenada cae adentro del polígono' : '');

  return (
    <span
      title={detalle}
      className={`flex items-center gap-1 text-[11px] ${
        dudoso ? 'text-brand-gray-400' : 'text-brand-gray-500'}`}
    >
      <MapTrifold size={12} weight="bold" />
      <span className={dudoso
        ? 'font-mono border-b border-dashed border-brand-gray-300'
        : 'font-mono font-semibold'}>
        {dudoso ? `≈ ${pieza.lote}` : pieza.lote}
      </span>
    </span>
  );
}
